/* <rowdy-drawer> — off-canvas panel used by the mobile menu, search and cart.
   Behaviour per CLAUDE.md: focus trap, Esc closes, focus returns to the
   trigger, aria-expanded / aria-controls kept in sync. No dependencies. */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class RowdyDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.drawer__panel');
    this.trigger = null;
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]')) this.close();
    });
    this.addEventListener('keydown', (e) => this.onKeydown(e));
  }

  get isOpen() { return this.hasAttribute('open'); }

  open(trigger) {
    if (this.isOpen) return;
    this.trigger = trigger || document.activeElement;
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.syncTriggers(true);
    const first = this.querySelector('[autofocus]') || this.panel;
    requestAnimationFrame(() => first && first.focus({ preventScroll: true }));
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.syncTriggers(false);
    if (this.trigger && typeof this.trigger.focus === 'function') this.trigger.focus({ preventScroll: true });
    this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
  }

  syncTriggers(open) {
    document.querySelectorAll(`[data-drawer-open="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', String(open)));
  }

  onKeydown(e) {
    if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
    if (e.key !== 'Tab') return;
    const items = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
}

if (!customElements.get('rowdy-drawer')) customElements.define('rowdy-drawer', RowdyDrawer);

/* Global trigger delegation: any element with data-drawer-open="<id>" */
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-drawer-open]');
  if (!trigger) return;
  const drawer = document.getElementById(trigger.dataset.drawerOpen);
  if (!drawer || typeof drawer.open !== 'function') return; // no JS drawer → follow the link
  e.preventDefault();
  document.querySelectorAll('rowdy-drawer[open]').forEach((d) => d !== drawer && d.close());
  drawer.open(trigger);
});

export { RowdyDrawer };
